import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { correctionsApi } from '../../api/corrections'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { CorrectionThread } from './CorrectionThread'
import { EntityPicker, type Picked } from './EntityPicker'
import { usePresetsFor } from './usePresets'
import { MIN_REASON, needsTarget, withTarget, type CorrectionView, type Preset } from '../../types/corrections'
import './correct.css'

// The correction affordance, on every assertion Scout makes. One component, one vocabulary: the presets
// come from the server (CorrectionPresets) so the chips a person clicks are the exact values the model and
// the nightly loop read back. The reason stays REQUIRED — it is the training signal, and the API refuses
// the call without one — and Discuss opens the thread for the times no preset and no sentence will do.
//
// Read and validate, never type out (DEC-2026-09-10-02): the reasons people actually give are one click
// each (the presets' hints), and the box is there for the one nobody guessed.

export interface CorrectProps {
  /** the correction kind for this assertion: lead, company-kind, next-step, threat, … */
  kind: string
  entityId: string
  /** what the row says on screen, so the ledger reads as English rather than as ids */
  entityLabel?: string
  /** what Scout currently says; kept on the correction so the mistake stays inspectable */
  currentValue?: string | null
  /** a quiet glyph for a row, rather than a visible button for a header */
  compact?: boolean
  label?: string
  /** extra classes on the trigger, for a host that places it in a cell of its own */
  className?: string
  onDone?: (correction: CorrectionView) => void
}

/** The caches a correction can change. Broad on purpose: a re-filed company moves several screens. */
const TOUCHED = ['deals', 'deals-stats', 'suggestions', 'corrections', 'correction', 'lead', 'accounts', 'account',
  'contacts', 'contact', 'threats', 'meetings', 'weekly', 'brief', 'improve', 'ledger', 'landscape',
  // a research correction marks the note, so the screen it was made on has to re-read it
  'outreach', 'outreach-person']

export function Correct({ kind, entityId, entityLabel, currentValue, compact = false, label, className, onDone }: CorrectProps) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement>(null)

  // A row is a link: a press on the affordance must not also open the row behind it.
  const stop = (ev: { stopPropagation: () => void }) => ev.stopPropagation()

  useEffect(() => {
    if (!open) return
    const away = (ev: MouseEvent) => { if (box.current && !box.current.contains(ev.target as Node)) setOpen(false) }
    const escape = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', escape) }
  }, [open])

  return (
    <span className={`correct${compact ? ' compact' : ''} ${className ?? ''}`} ref={box} onClick={stop}>
      <button
        type="button"
        className={compact ? 'correct-glyph' : 'button small correct-open'}
        aria-expanded={open}
        aria-label={compact ? `Not right? Correct what Scout says about ${entityLabel ?? 'this'}` : undefined}
        onClick={(ev) => { stop(ev); setOpen((v) => !v) }}
        title="Tell Scout this is wrong. A reason is required — it is what the nightly improvement run learns from."
      >
        {compact ? <CorrectMark /> : (label ?? 'Not right?')}
      </button>
      {open && (
        <CorrectPanel
          kind={kind}
          entityId={entityId}
          entityLabel={entityLabel}
          currentValue={currentValue}
          onClose={() => setOpen(false)}
          onRecorded={onDone}
        />
      )}
    </span>
  )
}

/** The glyph a row carries: a struck-through tick. Quiet until the row is hovered (see correct.css). */
function CorrectMark() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 13l4 4L20 5" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

export interface CorrectPanelProps {
  kind: string
  entityId: string
  entityLabel?: string
  currentValue?: string | null
  onClose: () => void
  onRecorded?: (correction: CorrectionView) => void
}

/** The panel itself: the kind's presets, the target picker where a preset points at one, the required
 *  reason, and — once recorded — the door into the discussion. Exported so it can be rendered directly. */
export function CorrectPanel({ kind, entityId, entityLabel, currentValue, onClose, onRecorded }: CorrectPanelProps) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const { presets, loading, error } = usePresetsFor(kind)
  const [chosen, setChosen] = useState<Preset | null>(null)
  const [target, setTarget] = useState<Picked | null>(null)
  const [reason, setReason] = useState('')
  const [recorded, setRecorded] = useState<CorrectionView | null>(null)
  const [discussing, setDiscussing] = useState(false)
  const box = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { box.current?.focus() }, [])

  const save = useMutation({
    mutationFn: () => correctionsApi.record(spoke, {
      entityKind: kind,
      entityId,
      fromValue: currentValue ?? null,
      toValue: chosen && needsTarget(chosen) ? withTarget(chosen, target?.id ?? '') : chosen?.value ?? '',
      reason: reason.trim(),
    }),
    onSuccess: (c) => {
      setRecorded(c)
      for (const key of TOUCHED) void qc.invalidateQueries({ queryKey: [key] })
      onRecorded?.(c)
    },
  })

  const wantsTarget = !!chosen && needsTarget(chosen)
  const enough = !!chosen && reason.trim().length >= MIN_REASON && (!wantsTarget || !!target)
  const current = currentValue ? presets?.presets.find((p) => p.value === currentValue)?.label ?? currentValue : null
  // The hints double as the reasons people give over and over, so rejecting is one click and still carries its why.
  const quick = [...new Set((presets?.presets ?? []).map((p) => p.hint).filter((h): h is string => !!h))]

  if (recorded) {
    return (
      <div className="correct-panel recorded" role="group" aria-label="Correction recorded">
        <div className="correct-head">
          <p className="kicker">On the ledger</p>
          <h3>Recorded, not yet learned</h3>
        </div>
        <p className="correct-note">
          Tonight's improvement run reads this, tries to work out the rule that would have caught it, and verifies that
          rule against every correction already on file. Until it does, nothing has changed but this one item.
        </p>
        <div className="correct-actions">
          <button type="button" className={`button small${discussing ? '' : ' primary'}`} onClick={() => setDiscussing((v) => !v)}>
            {discussing ? 'Hide the discussion' : 'Discuss'}
          </button>
          <Link className="button small" to="/improve">Open the ledger</Link>
          <button type="button" className="button small" onClick={onClose}>Close</button>
        </div>
        {discussing && <CorrectionThread correctionId={recorded.correctionId} compact />}
      </div>
    )
  }

  return (
    <div className="correct-panel" role="group" aria-label="Correct this">
      <div className="correct-head">
        <p className="kicker">{presets?.label ?? 'Correct this'}</p>
        {entityLabel && <h3 className="correct-subject">{entityLabel}</h3>}
        {current && <p className="correct-now">Scout says <strong>{current}</strong>. What is it really?</p>}
        {!current && <p className="correct-now">What has Scout got wrong here?</p>}
      </div>

      {loading && <p className="muted small">Loading the options…</p>}
      {error && <p className="error small">{error instanceof RefusedError ? error.refusal.message : error.message}</p>}
      {!loading && !error && !presets && (
        <p className="muted small">This deployment has no corrections defined for <span className="mono">{kind}</span>, so there is nothing to choose.</p>
      )}

      {presets && (
        <ul className="correct-presets">
          {/* Every option is offered, the current one included and marked. Hiding the current value
              produced a wrong correction on 2026-09-16: the screen had shown a recruiter among the
              prospects, the record already said vendor, the list left vendor out, and the person had to
              pick "partner" to say anything at all (PL-138). Choosing the current value records that the
              record is right and the SCREEN was wrong — a defect the ledger can then show as one. */}
          {presets.presets.map((p) => {
            const now = p.value === currentValue
            return (
              <li key={p.value}>
                <button
                  type="button"
                  className={`chip-filter ${chosen?.value === p.value ? 'on' : ''} ${now ? 'now' : ''}`}
                  onClick={() => { setChosen(p); setTarget(null); if (p.hint && !reason) setReason(now ? 'The record is right; the screen showed it as something else.' : p.hint) }}
                  title={now ? 'What Scout has on record now. Pick it if the screen showed something else.' : p.hint ?? undefined}
                >
                  {p.label}{now && <span className="muted small"> · on record now</span>}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {wantsTarget && presets?.targetKind && (
        <EntityPicker targetKind={presets.targetKind} exclude={entityId} picked={target} onPick={setTarget} />
      )}

      {quick.length > 0 && (
        <ul className="correct-quick">
          {quick.map((r) => (
            <li key={r}>
              <button type="button" className={`link-button small ${reason === r ? 'on' : ''}`} onClick={() => setReason(r)}>{r}</button>
            </li>
          ))}
        </ul>
      )}

      <textarea
        ref={box}
        className="correct-reason"
        rows={2}
        value={reason}
        onChange={(ev) => setReason(ev.target.value)}
        placeholder="Why is it wrong? Scout reads this back before it decides anything like it again."
        aria-label="Reason (required)"
      />

      <p className="correct-note muted small">
        {!chosen
          ? 'Pick what it really is first.'
          : wantsTarget && !target
            ? `Pick the ${presets?.targetKind === 'person' ? 'person' : 'company'} it belongs to.`
            : enough
              ? 'This goes on the self-improvement ledger. Tonight Scout works out the rule that would have caught it, and proves that rule against every correction already on file.'
              : 'A reason is required. Without it the same mistake comes back on the next scan.'}
      </p>

      {save.isError && <p className="error small">{save.error instanceof RefusedError ? save.error.refusal.message : (save.error as Error).message}</p>}

      <div className="correct-actions">
        <button type="button" className="button primary small" disabled={!enough || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Recording…' : 'Record it'}
        </button>
        <button type="button" className="button small" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

export { CorrectionThread } from './CorrectionThread'
export { CorrectionHistory } from './CorrectionHistory'
